import { InvalidParameterError } from "../../core/exception/invalid-parameter-error";
import { Logger } from "../../logger/logger";
import { RegexHelper } from "../../core/regex-helper";
import { BitConverter } from "../../helper/bit-converter";
import { UrlHelper } from "../../core/html/url-helper";
import { StringExtend } from "../../extend/string-extend";
import { IdName } from "../../types/interfaces";

type FilterUnaryOperator = "not";
type FilterBinaryOperator = "and" | "or";
type FilterOperator = "=" | "<>" | ">" | ">=" | "<" | "<=" | "startswith" | "endswith" | "contains" | "notcontains";

type FilterExpression
	= [FilterExpression, FilterBinaryOperator, FilterExpression]
	| [string | boolean, FilterOperator, any]
	| [FilterUnaryOperator, FilterExpression];

export class FilterBuilder {
	public static getRegexFilterExpression = () => function (value: any, operation: any) {
		let column = this; // eslint-disable-line

		let lastRule: any = null;
		let lastRegex: RegExp;

		let matchRuleShort = (str: any, rule: string) => {
			if (!lastRule || lastRule !== rule) {
				lastRegex = RegexHelper.getRegexFromWildcard(rule, "i");
			}
			return lastRegex.test(str);
		};
		if (value) {
			if (value.indexOf("*") === -1 && value.indexOf("?") === -1) {
				return column.defaultCalculateFilterExpression.apply(column, arguments); // eslint-disable-line
			} else {
				if (!operation) {
					// workaround caching
					let rand = Math.round(Math.random() * 10000);
					let foo = [() => rand, "=", rand];
					return [[(data: any) => matchRuleShort(column.calculateCellValue(data), value), "=", true], "and", foo];
				} else {
					return [column.dataField, operation, value];
				}
			}
		}
		return null;
	}

	public static getBitSetHeaderFilterExpression = (filterValue: number | string, target: string, items: IdName[], dataField: string) => {
		let filterBuilder = new FilterBuilder("or");
		if (!filterValue) {
			return filterBuilder.getFilter();
		}
		let searchId: number = filterValue as number;
		let searchIds: number[] = [searchId];
		if (target === "search" && typeof filterValue === "string") {
			let curItems = items;
			searchIds = curItems.filter((item) => item.name.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1).map((item) => item.id);
		}
		for (let fv of searchIds) {
			filterBuilder.addExpression([dataField, "contains", fv]);
		}
		return filterBuilder.getFilter();
	}

	private static isNestedArrayLengthGreaterThen = (input: any[], length: number) => {
		let count = 0;
		let nextPass: any[][] = [input];
		while (nextPass.length > 0) {
			let currentPass = nextPass;
			nextPass = [];
			for (let item of currentPass) {
				if (Array.isArray(item)) {
					nextPass = nextPass.concat(item);
				} else {
					count++;
				}
				if (count >= length) {
					return true;
				}
			}
		}

		return false;
	}

	private filters: FilterExpression[] = [];

	constructor(private concatType: FilterBinaryOperator) { }

	public hasFilterExpressions() {
		return this.filters.length > 0;
	}

	public addWildcardExpression(expression: FilterExpression): FilterBuilder {
		let curExpression = expression[2];
		if (typeof curExpression !== "string" || StringExtend.isNullOrEmpty(curExpression) || curExpression.indexOf("*") === -1) {
			return this.addExpression(expression);
		}
		let filterExpression = expression[1];
		let filterKey = expression[0];
		if (curExpression === "*") {
			if (this.concatType === "and") {
				return this;
			} else if (this.concatType === "or") {
				this.addExpression([true, "=", true]);
				return this;
			}
		}

		let splitCurExpression = curExpression.split("*");
		switch (filterExpression) {
			case "=":
				let newStartExpression: any = [filterKey, "startswith", splitCurExpression[0]];
				let newEndExpression: any = [filterKey, "endswith", splitCurExpression[splitCurExpression.length - 1]];
				this.addExpression(newStartExpression);
				if (splitCurExpression.length > 2) {
					for (let i = 1; i < splitCurExpression.length - 1; i++) {
						let curExpr: any = [filterKey, "contains", splitCurExpression[i]];
						this.addExpression(curExpr);
					}
				}
				this.addExpression(newEndExpression);
				break;
			case "contains":
				let index = 0;
				for (let expr of splitCurExpression) {
					let newExpression: FilterExpression;
					if (index === 0) {
						newExpression = [filterKey, "startswith", expr] as any;
					} else if (index + 1 === splitCurExpression.length) {
						newExpression = [filterKey, "endswith", expr] as any;
					} else {
						newExpression = [filterKey, "contains", expr] as any;
					}
					index++;
					this.addExpression(newExpression);
				}
				break;
			default:
				throw new Error("Not implemented expression");
		}

		return this;
	}

	public getTreeFilter(items?: FilterExpression[]): FilterExpression {
		let curItems: FilterExpression[] = items || this.filters;

		let result: FilterExpression = null;
		let count = curItems.length;
		let isEven = count % 2 === 0;
		let left = isEven ? (count / 2) : ((count - 1) / 2);
		if (left > 1) {
			return [
				this.getTreeFilter(curItems.slice(0, left)), this.concatType,
				this.getTreeFilter(curItems.slice(left))
			];
		}

		for (let filter of curItems) {
			if (result == null) {
				result = filter;
				continue;
			}
			result = [result, this.concatType, filter];
		}
		return result;
	}

	public getFilter(): FilterExpression {
		let result: FilterExpression = null;
		if (FilterBuilder.isNestedArrayLengthGreaterThen(this.filters, 100)) {
			Logger.warnWithTrace("Query more than hundred filters");
		}

		for (let filter of this.filters) {
			if (result == null) {
				result = filter;
			} else {
				result = [result, this.concatType, filter];
			}
		}

		return result;
	}

	public addExpression(expression: FilterExpression): this {
		if (!expression) {
			throw new InvalidParameterError("item", InvalidParameterError.nullText);
		} else if (expression.length !== 3) {
			throw new InvalidParameterError("item", "The array length should be three");
		}
		this.filters.push(expression);
		return this;
	}

	public addFilterBuilder(filterBuilder: FilterBuilder): this {
		let newFilters = filterBuilder.getFilter();
		if (newFilters) {
			this.filters.push(newFilters);
		}

		return this;
	}

	public addTreeFilterBuilder(filterBuilder: FilterBuilder): this {
		let newFilters = filterBuilder.getTreeFilter();
		if (newFilters) {
			this.filters.push(newFilters);
		}
		return this;
	}

	public addUserMarketerPermissionExpression(marketerExpression: string): this {
		let mktIdSet = UrlHelper.getAllowedMarketerIds();
		return this.addMarketerPermissionExpression(mktIdSet, marketerExpression);
	}

	public addMarketerPermissionExpression(mktIdSet: number, marketerExpression: string): this {
		let arr = BitConverter.toBitField(mktIdSet);
		if (arr.length === 0) {
			return this.addExpression([true, "=", false]);
		}

		let builder = new FilterBuilder("or");
		for (let mktId of arr) {
			this.addExpression([`${marketerExpression}/any(id: id eq ${mktId})`, "=", true]);
		}
		return this.addFilterBuilder(builder);
	}
}